import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Upload,
  message,
  Row,
  Col,
  TreeSelect,
  Input,
  Table,
  Tooltip,
  Radio,
  Checkbox,
  Select,
} from 'antd';
import { useMutation, useReactiveVar } from '@apollo/client';
import PropTypes from 'prop-types';

import { userVar } from 'graphql/cache';
import {
  ADD_CATALOG_PRODUCTS,
  PARSE_XML_CATALOG,
  SAVE_CATALOG_XML_LINK,
} from 'graphql/mutations/contentLibraryMutations';
import { CURRENCY_FORMATS } from 'constants/currency';
import {
  ChevronDownIcon,
  UploadCloudIcon,
  DocumentIcon,
  CloseIcon,
  AddFolder,
  FolderIcon,
} from 'components/Icons';
import Popconfirm from 'components/PopConfirm';
import helpers from 'helpers/proposal';
import commonHelpers from 'helpers/index';

import './ImportCatalogModal.scss';

const { Dragger } = Upload;

/**
 * Image Cell Component with CORS error handling
 * Handles images that may fail to load due to CORS restrictions
 */
const ImageCell = ({ images }) => {
  const [imageError, setImageError] = React.useState(false);
  const [imageUrl, setImageUrl] = React.useState(null);

  React.useEffect(() => {
    setImageError(false);
    let url = null;

    // If it's an array of objects
    if (Array.isArray(images) && images.length > 0) {
      const [firstImage] = images;
      if (typeof firstImage === 'object' && firstImage !== null && 'url' in firstImage) {
        const { url: imgUrl } = firstImage;
        url = imgUrl;
      } else if (typeof firstImage === 'string') {
        url = firstImage;
      }
    } else if (typeof images === 'string') {
      // If it's a simple string/URL or comma-separated URLs
      const imageUrls = images
        .split(',')
        .map((u) => u.trim())
        .filter((u) => u);
      if (imageUrls.length > 0) {
        const [firstUrl] = imageUrls;
        url = firstUrl;
      }
    }

    setImageUrl(url);
  }, [images]);

  // Early return if no images
  if (!images || !imageUrl) {
    // If images is a string that's not a valid URL, show it as text
    if (typeof images === 'string' && !imageUrl) {
      return (
        <span className="images" title={images}>
          {images.length > 30 ? `${images.substring(0, 30)}...` : images}
        </span>
      );
    }
    return <span className="images">N/A</span>;
  }

  // If image failed to load, show fallback
  if (imageError) {
    return (
      <span className="images" title={`Failed to load: ${imageUrl}`}>
        <span className="image-error">Image unavailable</span>
      </span>
    );
  }

  return (
    <span className="images">
      <img
        src={imageUrl}
        alt="product"
        loading="lazy"
        onError={() => {
          setImageError(true);
        }}
        onLoad={() => {
          setImageError(false);
        }}
      />
    </span>
  );
};

ImageCell.propTypes = {
  images: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
};

/**
 * Helper function to detect currency from value and clean the amount
 * @param {string} value - The value to check for currency
 * @param {string} currency - Currency code
 * @returns {Object} - { amount: string, unit: string }
 */
const detectCurrency = (value, currency) => {
  if (!value) return { amount: '0', unit: null };

  const stringValue = value.toString().trim();

  // Check for percentage first
  if (stringValue.includes('%')) {
    return {
      amount: stringValue.replace('%', '').trim() || '0',
      unit: '%',
    };
  }

  // Check for currency symbols and codes from CURRENCY_FORMATS
  for (const currencyFormat of CURRENCY_FORMATS) {
    const { symbol, code } = currencyFormat;

    // Check if the value starts with the currency symbol
    if (stringValue.startsWith(symbol)) {
      const amount = stringValue.replace(symbol, '').trim() || '0';
      return {
        amount: commonHelpers.cleanAmountFromPriceSeparator(amount),
      };
    }

    // Check if the value ends with the currency code (e.g., "100 USD")
    if (stringValue.endsWith(code)) {
      const amount = stringValue.replace(code, '').trim() || '0';
      return {
        amount: commonHelpers.cleanAmountFromPriceSeparator(amount),
      };
    }

    // Check if the value contains the currency code with space (e.g., "100 USD")
    if (stringValue.includes(` ${code}`)) {
      const amount = stringValue.replace(` ${code}`, '').trim() || '0';
      return {
        amount: commonHelpers.cleanAmountFromPriceSeparator(amount),
      };
    }

    // Check for currency code in parentheses (e.g., "100 (USD)")
    if (stringValue.includes(`(${code})`)) {
      const amount = stringValue.replace(`(${code})`, '').trim() || '0';
      return {
        amount: commonHelpers.cleanAmountFromPriceSeparator(amount),
      };
    }

    // Check for currency code with dash (e.g., "100-USD")
    if (stringValue.includes(`-${code}`)) {
      const amount = stringValue.replace(`-${code}`, '').trim() || '0';
      return {
        amount: commonHelpers.cleanAmountFromPriceSeparator(amount),
      };
    }
  }

  // If no currency detected, assume it's a plain number
  const amount = stringValue || '0';
  return {
    amount: commonHelpers.cleanAmountFromPriceSeparator(amount),
  };
};

/**
 * Wrapper function for concatcurr similar to TeXHTMLString.js
 * Provides consistent currency formatting across the application
 *
 * @param {number|string} value - The amount to format
 * @param {Object} options - Formatting options
 * @param {Object} options.prop - Proposal object for price separator settings
 * @param {string} options.curr - Currency symbol/code
 * @param {number} options.limitDec - Decimal precision limit
 * @param {boolean} options.roundedTotal - Whether to round to whole numbers
 * @param {boolean} options.parsingDecimal - Whether parsing decimal input
 * @param {number} options.minimum - Minimum value (0 for no negative)
 * @param {number} options.y - Multiplier for quantity calculations
 * @returns {string} - Formatted currency string
 */
const concatCurrency = (value = 0, options = {}) => {
  const {
    prop = null,
    curr = '$',
    limitDec,
    roundedTotal = true,
    parsingDecimal = false,
    y,
  } = options;

  return helpers.concatcurr({
    prop,
    x: value,
    y,
    curr,
    limitDec,
    roundedTotal,
    parsingDecimal,
  });
};

/**
 * Upload content library item
 *
 * @param {Object} props
 * @param {Function} props.cancelUploadModal
 * @param {string} props.selectedCoverType
 * @param {boolean} props.showUploadModal
 * @param {string} props.selectedFolderKey
 * @param {Function} props.onSuccess
 * @param {string} props.type
 */
const ImportCatalogModal = ({
  cancelUploadModal,
  showUploadModal,
  showImportCatalogModal,
  handleTreeSearch,
  addNewChildFromSearch,
  treeData,
  refetchCatalogs,
  selectedFolderKey,
  defaultCatalogSource = 'csv',
  defaultSaveXmlLink = false,
  setTreeSearch,
}) => {
  const user = useReactiveVar(userVar);
  const predefinedFields = [
    'name',
    'description',
    'quantity',
    'price',
    'discount',
    'paymentType',
    'duration',
    'images',
  ];

  const [fileList, setFileList] = useState([]);
  const [xmlFileList, setXmlFileList] = useState([]);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [catalogSource, setCatalogSource] = useState(defaultCatalogSource);
  const [xmlLink, setXmlLink] = useState('');
  const [localSelectedFolderKey, setLocalSelectedFolderKey] = useState('');
  const [treeSelectValue, setTreeSelectValue] = useState(null);
  const [newlyCreatedFolderName, setNewlyCreatedFolderName] = useState(null);
  const [products, setProducts] = useState([]);
  const [saveXmlLink, setSaveXmlLink] = useState(defaultSaveXmlLink);
  const [savingXmlLink, setSavingXmlLink] = useState(false);
  const [selectedHeaders, setSelectedHeaders] = useState({
    images: null,
    name: null,
    description: null,
    quantity: null,
    duration: null,
    price: null,
    discount: null,
    paymentType: null,
  });

  const [addCatalog, { loading: addCatalogLoading }] = useMutation(ADD_CATALOG_PRODUCTS);
  const [parseXMLCatalog, { loading: parseXMLLoading }] = useMutation(PARSE_XML_CATALOG);
  const [saveXMLLinkMutation] = useMutation(SAVE_CATALOG_XML_LINK);

  /**
   * Handle key command
   *
   * @param {Object} e
   */
  const handleKeyCommand = (e) => {
    if (e.key === 'Escape') {
      setShowModal(true);
      const isNotCombinedKey = !(e.ctrlKey || e.altKey || e.shiftKey);
      if (isNotCombinedKey) {
        if (fileList.length > 0 && showUploadModal) {
          makeEscapeModal(true);
        }
      }
    }
  };

  const makeEscapeModal = (showModal) => {
    setShowPromptModal(showModal);
    setShowModal(showModal);
  };

  useEffect(() => {
    if (!showPromptModal) {
      window.addEventListener('keydown', handleKeyCommand, true);
    } else {
      window.removeEventListener('keydown', handleKeyCommand, true);
    }
    setShowPromptModal(!showPromptModal); // eslint-disable-next-line
  }, []);

  /**
   * Add products to catalog
   */
  const addProductsToCatalog = async () => {
    // Process original products to preserve access to all fields during transformation
    // Apply header mapping at the end to only include selected fields
    const productsToAdd = products.map((originalProduct) => {
      // Access fields using selectedHeaders mapping to get values from original product
      const getFieldValue = (headerKey) => {
        const actualKey = selectedHeaders[headerKey];
        return actualKey !== null && actualKey !== undefined
          ? originalProduct[actualKey]
          : undefined;
      };

      // Get values from original product using header mapping
      const quantity = getFieldValue('quantity');
      const duration = getFieldValue('duration');
      const paymentType = getFieldValue('paymentType');
      const price = getFieldValue('price');
      const discount = getFieldValue('discount');
      const images = getFieldValue('images');
      const name = getFieldValue('name');
      const description = getFieldValue('description');

      // Handle price - if it's a simple value, convert to object structure
      let priceObj = {};
      if (price) {
        if (typeof price === 'object' && price !== null) {
          // Already an object, use it
          priceObj = { ...price };
        } else {
          // Simple value, detect currency and create object
          const detectedPrice = detectCurrency(price.toString(), user.currency);
          priceObj = {
            amount: detectedPrice.amount,
          };
        }
      } else {
        // No price, set default
        priceObj = {
          amount: '0',
        };
      }

      // Ensure price object has required structure
      const finalPrice = {
        ...priceObj,
        items: parseFloat(quantity) || 1,
        quantity: parseFloat(duration) || 1,
        type: paymentType || '',
      };

      // Handle discount - if it's a simple value, convert to object structure
      let finalDiscount = null;
      if (discount) {
        if (typeof discount !== 'object' || discount === null) {
          // Simple value, detect currency and create object
          const detectedDiscount = detectCurrency(discount.toString(), user.currency);
          finalDiscount = {
            value: detectedDiscount.amount || '0',
            unit: detectedDiscount.unit,
          };
        } else {
          finalDiscount = discount;
        }
      }

      // Handle images - if it's a simple string/URL, convert to array of objects
      let finalImages = [];
      if (images) {
        if (typeof images === 'string') {
          // Simple string/URL or comma-separated URLs
          const imageUrls = images
            .split(',')
            .map((url) => url.trim())
            .filter((url) => url);
          finalImages = imageUrls.map((imageUrl) => ({
            url: imageUrl,
            name: imageUrl.split('/').pop() || 'image',
          }));
        } else if (Array.isArray(images)) {
          // Ensure all items are objects with url property
          finalImages = images
            .map((img) => {
              if (typeof img === 'object' && img !== null && 'url' in img) {
                return img;
              }
              // If it's a string in the array, convert to object
              if (typeof img === 'string') {
                return {
                  url: img,
                  name: img.split('/').pop() || 'image',
                };
              }
              return img;
            })
            .filter((img) => img && img.url);
        }
      }

      // Build the final product object with only selected fields
      const product = {};

      // Only include fields that were selected (not null/undefined in selectedHeaders)
      if (selectedHeaders.name !== null && selectedHeaders.name !== undefined) {
        product.name = name;
      }
      if (selectedHeaders.description !== null && selectedHeaders.description !== undefined) {
        product.description = description;
      }
      if (selectedHeaders.price !== null && selectedHeaders.price !== undefined) {
        product.price = finalPrice;
      }
      if (
        selectedHeaders.discount !== null &&
        selectedHeaders.discount !== undefined &&
        finalDiscount
      ) {
        product.discount = finalDiscount;
      }
      if (selectedHeaders.images !== null && selectedHeaders.images !== undefined) {
        product.images = finalImages;
      }

      return product;
    });

    try {
      await addCatalog({
        variables: {
          products: productsToAdd,
          folderKey: localSelectedFolderKey || selectedFolderKey,
        },
      });

      // Save XML link if checkbox is checked and catalog source is XML
      if (saveXmlLink && catalogSource === 'xml' && xmlLink) {
        setSavingXmlLink(true);
        await saveXMLLinkMutation({
          variables: {
            xmlLink: {
              xmlUrl: xmlLink,
              folderKey: localSelectedFolderKey || selectedFolderKey,
              autoSync: false,
              fieldMappings: JSON.stringify(selectedHeaders),
            },
          },
          onCompleted: () => {
            setSavingXmlLink(false);
            message.success('Products added and XML link saved successfully');
          },
          onError: (error) => {
            console.error('Failed to save XML link:', error);
            setSavingXmlLink(false);
            message.warning('Products added but failed to save XML link');
          },
        });
      }

      onCancelModal();
      refetchCatalogs();
    } catch (error) {
      message.error(error.message);
      onCancelModal();
    }
  };

  /**
   * Reset state when modal opens/closes
   */
  useEffect(() => {
    if (showImportCatalogModal) {
      // Reset to defaults when modal opens
      setCatalogSource(defaultCatalogSource);
      setSaveXmlLink(defaultSaveXmlLink);
      setFileList([]);
      setXmlFileList([]);
      setProducts([]);
      setXmlLink('');
    }
  }, [showImportCatalogModal, defaultCatalogSource, defaultSaveXmlLink]);

  /**
   * On cancel modal
   */
  const onCancelModal = () => {
    setFileList([]);
    setXmlFileList([]);
    setProducts([]);
    setXmlLink('');
    setCatalogSource(defaultCatalogSource);
    setSaveXmlLink(defaultSaveXmlLink);
    cancelUploadModal();
  };

  /**
   * Parse XML file content and create data URL
   */
  const parseXMLFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const xmlContent = e.target.result;

          // Validate XML by parsing it
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

          // Check for parsing errors
          const parseError = xmlDoc.querySelector('parsererror');
          if (parseError) {
            reject(new Error('Invalid XML file format'));
            return;
          }

          // Convert XML to string and create a data URL
          const serializer = new XMLSerializer();
          const xmlString = serializer.serializeToString(xmlDoc);

          // Create data URL that can be used with the backend
          const dataUrl = `data:application/xml;base64,${btoa(
            unescape(encodeURIComponent(xmlString))
          )}`;

          resolve(dataUrl);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  /**
   * Handle XML file upload
   */
  const handleXMLFileUpload = async (file = null) => {
    const fileToProcess =
      file || (xmlFileList.length > 0 ? xmlFileList[0].originFileObj || xmlFileList[0] : null);

    if (!fileToProcess) {
      message.error('Please upload an XML file');
      return;
    }

    try {
      // Read and parse the XML file
      const dataUrl = await parseXMLFile(fileToProcess);

      // Use the existing mutation with the data URL
      const { data } = await parseXMLCatalog({
        variables: { xmlUrl: dataUrl },
      });

      if (data?.parseXMLCatalog && data.parseXMLCatalog.length > 0) {
        const mergedProducts = mergeAdditionalFields(data.parseXMLCatalog);
        setProducts(mergedProducts);
        detectAndUpdateHeaders(mergedProducts);
        setXmlFileList([]);
        message.success(`Successfully parsed ${data.parseXMLCatalog.length} products from XML`);
      } else {
        message.error('No products found in XML');
      }
    } catch (error) {
      message.error(error.message || 'Failed to parse XML file');
    }
  };

  /**
   * Handle XML upload (from link)
   */
  const handleXMLUpload = async () => {
    // If file is uploaded, use file upload handler
    if (xmlFileList.length > 0) {
      await handleXMLFileUpload();
      return;
    }

    // Otherwise, use link
    if (!xmlLink) {
      message.error('Please enter an XML link or upload an XML file');
      return;
    }

    try {
      const { data } = await parseXMLCatalog({
        variables: { xmlUrl: xmlLink },
      });

      if (data?.parseXMLCatalog && data.parseXMLCatalog.length > 0) {
        const mergedProducts = mergeAdditionalFields(data.parseXMLCatalog);
        setProducts(mergedProducts);
        detectAndUpdateHeaders(mergedProducts);
        message.success(`Successfully parsed ${data.parseXMLCatalog.length} products from XML`);
      } else {
        message.error('No products found in XML');
      }
    } catch (error) {
      message.error(error.message || 'Failed to parse XML');
    }
  };

  /**
   * Handle upload
   */
  const handleUpload = async () => {
    if (catalogSource === 'csv') {
      await handleCSVUpload();
    } else {
      await handleXMLUpload();
    }
  };

  /**
   * Props for the uploader
   */
  const props = {
    multiple: false,
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file, files) => {
      setFileList([...fileList, ...files]);
      return false;
    },
    fileList,
  };

  /**
   * On select tree item
   *
   * @param {Object} selectedTree
   */
  const handleSelectTreeItem = (selectedTree) => {
    const folderKey = selectedTree?.[0] || '';
    setLocalSelectedFolderKey(folderKey);
    const treeKey = getFolderKeyFromTreeData(folderKey, treeData?.children || []);
    setTreeSelectValue(treeKey);
  };

  /**
   * Initialize tree select value when treeData or selectedFolderKey changes
   * Only updates if not currently selecting a newly created folder
   */
  useEffect(() => {
    if (treeData?.children && !newlyCreatedFolderName) {
      const folderKeyToUse = localSelectedFolderKey || selectedFolderKey;
      if (folderKeyToUse) {
        const initialKey = getFolderKeyFromTreeData(folderKeyToUse, treeData.children);
        if (initialKey) {
          setTreeSelectValue(initialKey);
        }
      } else if (treeSelectValue === null) {
        // Reset to null if no folder is selected
        setTreeSelectValue(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData, selectedFolderKey]);

  /**
   * Effect to automatically select newly created folder
   */
  useEffect(() => {
    if (newlyCreatedFolderName && treeData?.children) {
      const foundFolder = findFolderByTitle(newlyCreatedFolderName, treeData.children);
      if (foundFolder) {
        setLocalSelectedFolderKey(foundFolder.path);
        setTreeSelectValue(foundFolder.key);
        setNewlyCreatedFolderName(null); // Reset after selection
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData, newlyCreatedFolderName]);

  /**
   * Parse CSV file and convert to JSON (Enhanced with preprocessing)
   *
   * @param {File} file - CSV file
   * @returns {Promise<Object>} Object with products array
   */
  const parseCSVToJSON = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const rawCsvText = event.target.result;

          // Preprocess the CSV to fix common issues
          const csvText = commonHelpers.preprocessCSV(rawCsvText);
          const lines = csvText.split('\n');

          // Parse CSV headers properly
          const headers = commonHelpers
            .parseCSVLine(lines[0])
            .map((header) => header.trim().toLowerCase());

          if (headers.length === 0) {
            throw new Error('No headers found in CSV file');
          }

          const products = [];
          let skippedRows = 0;

          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            const values = commonHelpers.parseCSVLine(line);

            // Skip rows that don't have enough fields or are clearly invalid
            if (values.length < Math.ceil(headers.length / 2)) {
              skippedRows++;
              continue;
            }

            const product = {};
            let hasValidData = false;

            headers.forEach((header, index) => {
              const value = values[index]?.trim();
              // Use original header case for storage, but check lowercase for matching
              const headerLower = header.toLowerCase();

              if (value) {
                hasValidData = true;

                switch (headerLower) {
                  case 'price':
                  case 'price_amount':
                  case 'amount':
                  case 'priceamount':
                  case 'pricevalue':
                  case 'price_value':
                    // Handle price amount specifically
                    const detectedPrice = detectCurrency(value, user.currency);
                    product.price = {
                      amount: detectedPrice.amount,
                    };
                    break;
                  case 'discount':
                  case 'discount_amount':
                  case 'discountamount':
                  case 'discountvalue':
                  case 'discount_value':
                    // Handle discount value - could be just number or "10%" format
                    const detectedDiscount = detectCurrency(value, user.currency);
                    product.discount = {
                      value: detectedDiscount.amount || '0',
                      unit: detectedDiscount.unit,
                    };
                    break;
                  case 'name':
                  case 'product_name':
                  case 'productname':
                  case 'title':
                    product.name = value;
                    break;
                  case 'description':
                  case 'product_description':
                  case 'productdescription':
                    product.description = value;
                    break;
                  case 'quantity':
                    const qty = parseInt(value);
                    product.quantity = isNaN(qty) ? '0' : qty.toString();
                    break;
                  case 'duration':
                  case 'period':
                  case 'billing_cycles':
                    product.duration = value;
                    break;
                  case 'image':
                  case 'images':
                  case 'imageurl':
                  case 'image_url':
                  case 'images_link':
                    if (!product.images) {
                      product.images = [];
                    }

                    const imageUrls = value
                      .split(',')
                      .map((url) => url.trim())
                      .filter((url) => url);
                    imageUrls.forEach((imageUrl) => {
                      if (imageUrl) {
                        product.images.push({
                          url: imageUrl,
                          name: imageUrl.split('/').pop() || 'image',
                        });
                      }
                    });
                    break;
                  case 'payment_way':
                  case 'paymentway':
                  case 'payment_type':
                  case 'paymenttype':
                  case 'payment_method':
                  case 'paymentmethod':
                  case 'type':
                    product.paymentType = value;
                    break;
                  default:
                    // Store all other fields as-is (preserve original header case)
                    // This ensures all CSV columns are included in the product object
                    product[header] = value;
                    break;
                }
              } else {
                // Even if value is empty, store the field to preserve all columns
                // This helps maintain consistent column structure across products
                if (!(headerLower in product)) {
                  product[header] = value || '';
                }
              }
            });

            // Only add products that have at least a name or valid data
            if (hasValidData && (product.name || product.description)) {
              products.push(product);
            } else {
              skippedRows++;
            }
          }

          // Provide feedback about processing
          if (skippedRows > 0) {
            console.warn(`Skipped ${skippedRows} invalid or empty rows during CSV processing`);
          }

          if (products.length === 0) {
            throw new Error(
              'No valid products found in CSV file. Please check the file format and content.'
            );
          }

          resolve(products);
        } catch (error) {
          reject(new Error('Failed to parse CSV file: ' + error.message));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read CSV file'));
      };

      reader.readAsText(file);
    });
  };

  /**
   * Merge additionalFields into product objects for display
   * @param {Array} products - Array of products with potential additionalFields
   * @returns {Array} - Products with additionalFields merged
   */
  const mergeAdditionalFields = (products) => {
    if (!products || !Array.isArray(products)) {
      return products;
    }
    return products.map((product) => {
      if (product.additionalFields && typeof product.additionalFields === 'object') {
        // Merge additionalFields into the product object
        const { additionalFields, ...rest } = product;
        return {
          ...rest,
          ...additionalFields,
        };
      }
      return product;
    });
  };

  /**
   * Detect and update headers based on product structure
   * Only auto-selects fields that match known patterns
   * Leaves unmatched fields as null/undefined for user to select
   * @param {Array} products - Array of parsed products
   */
  const detectAndUpdateHeaders = (products) => {
    if (products.length === 0) return;

    // Get all unique keys from products
    const allKeys = new Set();
    products.forEach((product) => {
      Object.keys(product).forEach((key) => allKeys.add(key));
    });

    // Reset headers - only set matched ones, leave others as null
    const updatedHeaders = {
      images: null,
      name: null,
      description: null,
      quantity: null,
      duration: null,
      price: null,
      discount: null,
      paymentType: null,
    };

    // Check price structure - if price exists and has amount property, keep it
    // Otherwise, look for price-related columns
    const hasPriceObject = products.some(
      (product) => product.price && typeof product.price === 'object' && 'amount' in product.price
    );

    if (hasPriceObject) {
      // Price structure is correct, ensure header points to 'price'
      updatedHeaders.price = 'price';
    } else {
      // Look for columns that might contain price data
      const pricePatterns = [
        'price',
        'price_amount',
        'amount',
        'priceamount',
        'pricevalue',
        'price_value',
        'cost',
        'pricing',
      ];
      const foundPriceKey = Array.from(allKeys).find((key) =>
        pricePatterns.some((pattern) => key.toLowerCase().includes(pattern))
      );
      if (foundPriceKey) {
        updatedHeaders.price = foundPriceKey;
      }
    }

    // Check discount structure
    const hasDiscountObject = products.some(
      (product) =>
        product.discount && typeof product.discount === 'object' && 'value' in product.discount
    );

    if (hasDiscountObject) {
      updatedHeaders.discount = 'discount';
    } else {
      const discountPatterns = [
        'discount',
        'discount_amount',
        'discountamount',
        'discountvalue',
        'discount_value',
      ];
      const foundDiscountKey = Array.from(allKeys).find((key) =>
        discountPatterns.some((pattern) => key.toLowerCase().includes(pattern))
      );
      if (foundDiscountKey) {
        updatedHeaders.discount = foundDiscountKey;
      }
    }

    // Check name field
    if (allKeys.has('name')) {
      updatedHeaders.name = 'name';
    } else {
      const namePatterns = ['name', 'product_name', 'productname', 'title'];
      const foundNameKey = Array.from(allKeys).find((key) =>
        namePatterns.some((pattern) => key.toLowerCase().includes(pattern))
      );
      if (foundNameKey) {
        updatedHeaders.name = foundNameKey;
      }
    }

    // Check description field
    if (allKeys.has('description')) {
      updatedHeaders.description = 'description';
    } else {
      const descPatterns = ['description', 'product_description', 'productdescription'];
      const foundDescKey = Array.from(allKeys).find((key) =>
        descPatterns.some((pattern) => key.toLowerCase().includes(pattern))
      );
      if (foundDescKey) {
        updatedHeaders.description = foundDescKey;
      }
    }

    // Check quantity field
    if (allKeys.has('quantity')) {
      updatedHeaders.quantity = 'quantity';
    } else {
      const qtyPatterns = ['quantity', 'qty', 'items'];
      const foundQtyKey = Array.from(allKeys).find((key) =>
        qtyPatterns.some((pattern) => key.toLowerCase().includes(pattern))
      );
      if (foundQtyKey) {
        updatedHeaders.quantity = foundQtyKey;
      }
    }

    // Check duration field
    if (allKeys.has('duration')) {
      updatedHeaders.duration = 'duration';
    } else {
      const durationPatterns = ['duration', 'period', 'billing_cycles'];
      const foundDurationKey = Array.from(allKeys).find((key) =>
        durationPatterns.some((pattern) => key.toLowerCase().includes(pattern))
      );
      if (foundDurationKey) {
        updatedHeaders.duration = foundDurationKey;
      }
    }

    // Check paymentType field
    if (allKeys.has('paymentType')) {
      updatedHeaders.paymentType = 'paymentType';
    } else {
      const paymentPatterns = [
        'payment_way',
        'paymentway',
        'payment_type',
        'paymenttype',
        'payment_method',
        'paymentmethod',
        'type',
      ];
      const foundPaymentKey = Array.from(allKeys).find((key) =>
        paymentPatterns.some((pattern) => key.toLowerCase().includes(pattern))
      );
      if (foundPaymentKey) {
        updatedHeaders.paymentType = foundPaymentKey;
      }
    }

    // Check images structure - if images exists and is array of objects, keep it
    // Otherwise, look for image-related columns
    const hasImagesArray = products.some(
      (product) =>
        product.images &&
        Array.isArray(product.images) &&
        product.images.length > 0 &&
        typeof product.images[0] === 'object' &&
        'url' in product.images[0]
    );

    if (hasImagesArray) {
      // Images structure is correct, ensure header points to 'images'
      updatedHeaders.images = 'images';
    } else {
      // Look for columns that might contain image data
      const imagePatterns = [
        'image',
        'images',
        'imageurl',
        'image_url',
        'images_link',
        'img',
        'photo',
        'picture',
        'thumbnail',
      ];
      const foundImageKey = Array.from(allKeys).find((key) =>
        imagePatterns.some((pattern) => key.toLowerCase().includes(pattern))
      );
      if (foundImageKey) {
        updatedHeaders.images = foundImageKey;
      }
    }

    setSelectedHeaders(updatedHeaders);
  };

  /**
   * Handle CSV upload and parsing
   *
   * @returns {Promise<void>}
   */
  const handleCSVUpload = async () => {
    if (fileList.length === 0) {
      message.error('Please select a CSV file');
      return;
    }

    try {
      const [csvFile] = fileList;
      const products = await parseCSVToJSON(csvFile);
      if (products.length === 0) {
        message.error('No valid products found in CSV file');
        return;
      }

      setProducts(products);
      detectAndUpdateHeaders(products);

      message.success(`Successfully parsed ${products.length} products from CSV`);
    } catch (error) {
      message.error(error.message);
    }
  };

  /**
   * Get title from treeData based on folderKey
   * @param {string} folderKey - The folder key to search for
   * @param {Array} children - The tree children array
   * @returns {string} - The title of the folder
   */
  const getFolderKeyFromTreeData = (folderKey, children = []) => {
    for (const child of children) {
      if (child.path === folderKey) {
        return child.key;
      }
      if (child.children && child.children.length > 0) {
        const foundKey = getFolderKeyFromTreeData(folderKey, child.children);
        if (foundKey) {
          return foundKey;
        }
      }
    }
    return null;
  };

  /**
   * Find folder by title in treeData
   * @param {string} folderTitle - The folder title to search for
   * @param {Array} children - The tree children array
   * @returns {Object|null} - The folder object with key and path, or null if not found
   */
  const findFolderByTitle = (folderTitle, children = []) => {
    for (const child of children) {
      if (child.title === folderTitle && !child.create) {
        return { key: child.key, path: child.path };
      }
      if (child.children && child.children.length > 0) {
        const found = findFolderByTitle(folderTitle, child.children);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  /**
   * Get all available field options for dropdowns
   * Only includes predefined standard field types
   */
  const getColumnHeaderOptions = () => {
    return [
      { label: 'Name', value: 'name' },
      { label: 'Description', value: 'description' },
      { label: 'Quantity', value: 'quantity' },
      { label: 'Duration', value: 'duration' },
      { label: 'Price', value: 'price' },
      { label: 'Discount', value: 'discount' },
      { label: 'Payment Type', value: 'paymentType' },
      { label: 'Images', value: 'images' },
    ];
  };

  /**
   * Check if any predefined field is mapped to multiple columns
   * The requirement states: "user can select the two multple columns with same name
   * but won't be able to save until only one column has a specific name"
   *
   * This means: multiple columns can be selected to map to the same predefined field,
   * but saving should be disabled if any predefined field is mapped to more than one column.
   *
   * With the current structure (selectedHeaders maps predefined field -> column),
   * when a user selects the same predefined field for multiple columns, the last selection
   * overwrites previous ones. So the structure already enforces one column per predefined field.
   *
   * However, to properly validate, we check: are there any cases where the same column
   * is mapped to multiple predefined fields? That would mean one column provides data
   * for multiple fields, which should be fine. But the requirement might want to prevent this.
   *
   * Actually, re-interpreting: "multiple columns with same name" = multiple XML columns
   * mapped to same predefined field. "only one column has a specific name" = one column per predefined field.
   *
   * Since the structure already enforces this, the validation should check if any column
   * appears as a value multiple times in selectedHeaders (meaning multiple predefined fields
   * map to the same column). This might be what we want to prevent.
   */
  const hasDuplicatePredefinedFieldMappings = () => {
    const mappedColumns = Object.values(selectedHeaders).filter(
      (v) => v !== null && v !== undefined
    );

    // Check if any column is mapped to multiple predefined fields
    // (same column value appears multiple times in selectedHeaders)
    const columnCounts = {};
    mappedColumns.forEach((column) => {
      columnCounts[column] = (columnCounts[column] || 0) + 1;
    });

    // If any column is mapped to multiple predefined fields, that's a duplicate
    // This means one column provides data for multiple predefined fields, which we want to prevent
    return Object.values(columnCounts).some((count) => count > 1);
  };

  /**
   * Handle header select
   *
   * @param {string} key - The key of the header
   */
  const handleHeaderSelect = (header, key) => {
    setSelectedHeaders({ ...selectedHeaders, [header]: key });
  };

  /**
   * Get duplicate error class
   * @param {string} header - The header of the column
   * @returns {string} - The duplicate error class
   */
  const getDuplicateErrorClass = (header) => {
    return Object.values(selectedHeaders)
      .filter((v) => v !== null && v !== undefined)
      .filter((v, _, arr) => arr.indexOf(v) !== arr.lastIndexOf(v))
      .includes(selectedHeaders[header]) && selectedHeaders[header]
      ? 'duplicate-error'
      : '';
  };

  /**
   * Get field mapped value
   * @param {string} header - The header of the column
   * @returns {string} - The field mapped value
   */
  const getFieldMappedValue = (header) => {
    return predefinedFields.includes(selectedHeaders[header]) ? selectedHeaders[header] : null;
  };

  return (
    <>
      <Modal
        className={`confirm-modal upload-content-library-item-modal ${
          products.length > 0 ? 'reassign-columns-modal' : ''
        }`}
        footer={null}
        visible={showImportCatalogModal}
        onCancel={() => {
          fileList.length > 0 ? setShowModal(true) : onCancelModal;
        }}
        width={700}
        closeIcon={<CloseIcon className="close-icon" onClick={onCancelModal} />}
        centered>
        {products.length > 0 ? (
          <div>
            <h3 className="title">Reassign Columns</h3>
            <div className="divider" />
            <p className="reassign-columns-modal-description">
              We were unable to detect some of the columns you uploaded please reconfigure to the
              right ones
            </p>

            {/* Column Mapping Table */}
            <div className="column-mapping-table">
              <Table
                dataSource={products}
                pagination={false}
                size="small"
                className="import-catalog-table"
                columns={(() => {
                  // Get all unique keys from products
                  const allKeys = new Set();
                  products.forEach((product) => {
                    Object.keys(product).forEach((key) => allKeys.add(key));
                  });

                  // Define column configurations
                  const columnConfigs = {
                    images: {
                      title: (
                        <Select
                          className={`column-header-select ${getDuplicateErrorClass('images')}`}
                          options={[
                            { label: 'Select...', value: null },
                            ...getColumnHeaderOptions(),
                          ]}
                          value={getFieldMappedValue('images')}
                          onChange={(value) => handleHeaderSelect('images', value)}
                          suffixIcon={<ChevronDownIcon />}
                          getPopupContainer={() => document.querySelector('.column-mapping-table')}
                          placeholder="Select..."
                        />
                      ),
                      defaultValue: 'images',
                      render: (images) => {
                        return <ImageCell images={images} />;
                      },
                    },
                    name: {
                      title: (
                        <Select
                          className={`column-header-select ${getDuplicateErrorClass('name')}`}
                          options={[
                            { label: 'Select...', value: null },
                            ...getColumnHeaderOptions(),
                          ]}
                          value={getFieldMappedValue('name')}
                          onChange={(value) => handleHeaderSelect('name', value)}
                          suffixIcon={<ChevronDownIcon />}
                          getPopupContainer={() => document.querySelector('.column-mapping-table')}
                          placeholder="Select..."
                        />
                      ),
                      defaultValue: 'name',
                      render: (text) => {
                        return (
                          <Tooltip title={text}>
                            <span className="product-name">{text || 'N/A'}</span>
                          </Tooltip>
                        );
                      },
                    },
                    description: {
                      title: (
                        <Select
                          className={`column-header-select ${getDuplicateErrorClass(
                            'description'
                          )}`}
                          options={[
                            { label: 'Select...', value: null },
                            ...getColumnHeaderOptions(),
                          ]}
                          value={getFieldMappedValue('description')}
                          onChange={(value) => handleHeaderSelect('description', value)}
                          suffixIcon={<ChevronDownIcon />}
                          getPopupContainer={() => document.querySelector('.column-mapping-table')}
                          placeholder="Select..."
                        />
                      ),
                      defaultValue: 'description',
                      render: (text) => {
                        return (
                          <Tooltip title={text}>
                            <span className="description">{text || 'N/A'}</span>
                          </Tooltip>
                        );
                      },
                    },
                    quantity: {
                      title: (
                        <Select
                          className={`column-header-select ${getDuplicateErrorClass('quantity')}`}
                          options={[
                            { label: 'Select...', value: null },
                            ...getColumnHeaderOptions(),
                          ]}
                          value={getFieldMappedValue('quantity')}
                          onChange={(value) => handleHeaderSelect('quantity', value)}
                          suffixIcon={<ChevronDownIcon />}
                          getPopupContainer={() => document.querySelector('.column-mapping-table')}
                          placeholder="Select..."
                        />
                      ),
                      defaultValue: 'quantity',
                      align: 'right',
                      render: (text) => {
                        return <span className="amount-value">{text || '0'}</span>;
                      },
                    },
                    price: {
                      title: (
                        <Select
                          className={`column-header-select ${getDuplicateErrorClass('price')}`}
                          options={[
                            { label: 'Select...', value: null },
                            ...getColumnHeaderOptions(),
                          ]}
                          value={getFieldMappedValue('price')}
                          onChange={(value) => handleHeaderSelect('price', value)}
                          suffixIcon={<ChevronDownIcon />}
                          getPopupContainer={() => document.querySelector('.column-mapping-table')}
                          placeholder="Select..."
                        />
                      ),
                      defaultValue: 'price',
                      align: 'right',
                      render: (price) => {
                        // Handle both object and simple value cases
                        let amount = '0';
                        if (price) {
                          if (typeof price === 'object' && price !== null && 'amount' in price) {
                            amount = price.amount || '0';
                          } else {
                            // Simple value, detect currency
                            const detectedPrice = detectCurrency(price.toString(), user.currency);
                            amount = detectedPrice.amount || '0';
                          }
                        }
                        return (
                          <span className="price-value">
                            {concatCurrency(amount, {
                              curr: user.currency || '$',
                              prop: user?.profile?.priceSeperator || '.',
                            })}
                          </span>
                        );
                      },
                    },
                    duration: {
                      title: (
                        <Select
                          className={`column-header-select ${getDuplicateErrorClass('duration')}`}
                          options={[
                            { label: 'Select...', value: null },
                            ...getColumnHeaderOptions(),
                          ]}
                          value={getFieldMappedValue('duration')}
                          onChange={(value) => handleHeaderSelect('duration', value)}
                          suffixIcon={<ChevronDownIcon />}
                          getPopupContainer={() => document.querySelector('.column-mapping-table')}
                          placeholder="Select..."
                        />
                      ),
                      defaultValue: 'duration',
                      align: 'right',
                      render: (duration) => {
                        return <span>{duration || '1'}</span>;
                      },
                    },
                    discount: {
                      title: (
                        <Select
                          className={`column-header-select ${getDuplicateErrorClass('discount')}`}
                          options={[
                            { label: 'Select...', value: null },
                            ...getColumnHeaderOptions(),
                          ]}
                          value={getFieldMappedValue('discount')}
                          onChange={(value) => handleHeaderSelect('discount', value)}
                          suffixIcon={<ChevronDownIcon />}
                          getPopupContainer={() => document.querySelector('.column-mapping-table')}
                          placeholder="Select..."
                        />
                      ),
                      defaultValue: 'discount',
                      align: 'right',
                      render: (discount) => {
                        return (
                          <span className="discount-value">
                            {discount?.unit === '%'
                              ? `${discount.value || '0'}%`
                              : concatCurrency(discount?.value || '0', {
                                  curr: user.currency || '$',
                                  prop: user?.profile?.priceSeperator || '.',
                                })}
                          </span>
                        );
                      },
                    },
                    paymentType: {
                      title: (
                        <Select
                          className={`column-header-select ${getDuplicateErrorClass(
                            'paymentType'
                          )}`}
                          options={[
                            { label: 'Select...', value: null },
                            ...getColumnHeaderOptions(),
                          ]}
                          value={getFieldMappedValue('paymentType')}
                          onChange={(value) => handleHeaderSelect('paymentType', value)}
                          suffixIcon={<ChevronDownIcon />}
                          getPopupContainer={() => document.querySelector('.column-mapping-table')}
                          placeholder="Select..."
                        />
                      ),
                      defaultValue: 'paymentType',
                      align: 'right',
                      render: (text) => {
                        return <span className="payment-way">{text || 'One time payment'}</span>;
                      },
                    },
                  };

                  // Generate columns for all keys that exist in the data
                  const columns = [];

                  // Helper function to check if a field has data
                  const hasData = (key, value) => {
                    if (key === 'images') {
                      // Handle both array of objects and simple string/URL cases
                      if (!value) return false;
                      if (Array.isArray(value)) {
                        return (
                          value.length > 0 &&
                          value.some((img) => {
                            if (typeof img === 'object' && img !== null) {
                              return img.url || img;
                            }
                            return img;
                          })
                        );
                      }
                      if (typeof value === 'string') {
                        const imageUrls = value
                          .split(',')
                          .map((url) => url.trim())
                          .filter((url) => url);
                        return imageUrls.length > 0;
                      }
                      return false;
                    }
                    if (key === 'price') {
                      // Handle both object and simple value cases
                      if (!value) return false;
                      if (typeof value === 'object' && value !== null) {
                        return (
                          value.amount !== undefined && value.amount !== null && value.amount !== ''
                        );
                      }
                      return value !== '' && value !== null && value !== undefined;
                    }
                    if (key === 'discount') {
                      // Handle both object and simple value cases
                      if (!value) return false;
                      if (typeof value === 'object' && value !== null) {
                        return (
                          value.value !== undefined && value.value !== null && value.value !== ''
                        );
                      }
                      return value !== '' && value !== null && value !== undefined;
                    }

                    return value && value !== '';
                  };

                  // First, add predefined columns that have data
                  predefinedFields.forEach((key) => {
                    if (allKeys.has(key)) {
                      const hasFieldData = products.some((product) => hasData(key, product[key]));
                      if (hasFieldData && columnConfigs[key]) {
                        columns.push({
                          dataIndex: key,
                          key: key,
                          ...columnConfigs[key],
                        });
                      }
                    }
                  });

                  // Then, add all other fields from XML that aren't predefined
                  Array.from(allKeys)
                    .filter((key) => !predefinedFields.includes(key))
                    .forEach((key) => {
                      const hasFieldData = products.some((product) => {
                        const value = product[key];
                        return value !== null && value !== undefined && value !== '';
                      });
                      if (hasFieldData) {
                        // Find which standard field this XML field is mapped to (if any)
                        const mappedToStandardField = Object.entries(selectedHeaders).find(
                          ([_, xmlFieldKey]) => xmlFieldKey === key
                        )?.[0];

                        // Create a generic column configuration for unmapped fields
                        columns.push({
                          dataIndex: key,
                          key: key,
                          title: (
                            <Select
                              className={`column-header-select ${
                                Object.values(selectedHeaders)
                                  .filter((v) => v !== null && v !== undefined)
                                  .filter((v, _, arr) => arr.indexOf(v) !== arr.lastIndexOf(v))
                                  .includes(key) && key
                                  ? 'duplicate-error'
                                  : ''
                              }`}
                              options={[
                                { label: 'Select...', value: null },
                                ...getColumnHeaderOptions(),
                              ]}
                              value={mappedToStandardField || null}
                              onChange={(value) => {
                                // Map this XML field (key) to the selected standard field (value)
                                // Allow multiple columns to map to the same predefined field
                                const updatedHeaders = { ...selectedHeaders };

                                if (value) {
                                  // First, clear any existing mapping where this XML field was mapped
                                  Object.keys(updatedHeaders).forEach((headerKey) => {
                                    if (updatedHeaders[headerKey] === key) {
                                      updatedHeaders[headerKey] = null;
                                    }
                                  });

                                  // Set the new mapping: standard field -> XML field
                                  // Note: We allow multiple columns to map to the same predefined field
                                  updatedHeaders[value] = key;
                                } else {
                                  // Clear mapping if "Select..." is chosen
                                  Object.keys(updatedHeaders).forEach((headerKey) => {
                                    if (updatedHeaders[headerKey] === key) {
                                      updatedHeaders[headerKey] = null;
                                    }
                                  });
                                }
                                setSelectedHeaders(updatedHeaders);
                              }}
                              suffixIcon={<ChevronDownIcon />}
                              getPopupContainer={() =>
                                document.querySelector('.column-mapping-table')
                              }
                              placeholder="Select..."
                            />
                          ),
                          render: (text) => {
                            // Generic render for unmapped fields
                            if (text === null || text === undefined) {
                              return <span>N/A</span>;
                            }
                            if (typeof text === 'object') {
                              return <span>{JSON.stringify(text)}</span>;
                            }
                            return (
                              <Tooltip title={String(text)}>
                                <span className="product-name">{String(text)}</span>
                              </Tooltip>
                            );
                          },
                        });
                      }
                    });

                  return columns;
                })()}
              />
            </div>

            {/* Action Buttons */}
            <div className="table-actions">
              {catalogSource === 'xml' && (xmlLink || xmlFileList.length > 0) && (
                <Checkbox
                  className="save-xml-link-checkbox"
                  checked={saveXmlLink}
                  onChange={(e) => setSaveXmlLink(e.target.checked)}
                  disabled={xmlFileList.length > 0}>
                  {xmlFileList.length > 0
                    ? 'Save XML link for future syncing (only available for links)'
                    : 'Save XML link for future syncing'}
                </Checkbox>
              )}
              <Button className="cancel-btn" onClick={onCancelModal}>
                CANCEL
              </Button>
              <Button
                type="primary"
                className="update-proceed-btn"
                disabled={hasDuplicatePredefinedFieldMappings()}
                onClick={addProductsToCatalog}
                loading={addCatalogLoading || savingXmlLink}>
                UPDATE AND PROCEED
              </Button>
            </div>
          </div>
        ) : (
          <div className="select-image">
            <h3 className="title">IMPORT YOUR CATALOG</h3>
            <div className="divider" />

            <Row className="catalog-source-row">
              <span>Select the catalog source:</span>
              <Radio.Group
                options={[
                  { label: 'CSV', value: 'csv' },
                  { label: 'XML', value: 'xml' },
                ]}
                onChange={(e) => {
                  setCatalogSource(e.target.value);
                }}
                value={catalogSource}
                optionType="button"
              />
            </Row>
            {catalogSource === 'csv' ? (
              <Dragger accept={'.csv'} {...props}>
                <Col className="select-image-uploader">
                  <UploadCloudIcon />
                  <p className="select-image-title">Drag &amp; drop your CSV file</p>
                  <p className="select-image-details">Maximum 3MB</p>
                  <Row
                    className="select-image-upload-text"
                    onClick={() =>
                      window.open(
                        'https://dr08qaykn9isr.cloudfront.net/application/sample-catalog.csv'
                      )
                    }
                    style={{ cursor: 'pointer' }}>
                    <DocumentIcon className="select-image-upload-icon" />
                    Download CSV example file
                  </Row>
                </Col>
              </Dragger>
            ) : (
              <Row className="xml-link-row">
                <label>Paste your XML link</label>
                <Input
                  placeholder="Paste your XML link"
                  value={xmlLink}
                  onChange={(e) => {
                    setXmlLink(e.target.value);
                    // Clear file list when link is entered
                    if (e.target.value) {
                      setXmlFileList([]);
                    }
                  }}
                />
              </Row>
            )}

            <Row className="select-folder-row">
              <label>Select/create folder</label>
              <TreeSelect
                showSearch
                dropdownRender={(menu) => <div className="select-folder-tree-select">{menu}</div>}
                virtual={false}
                treeData={(treeData?.children || [])
                  .filter((item) => item.title !== null)
                  .map((item) => {
                    return {
                      ...item,
                      icon: item.create ? <AddFolder /> : <FolderIcon />,
                    };
                  })}
                treeIcon={true}
                value={
                  treeSelectValue !== null
                    ? treeSelectValue
                    : getFolderKeyFromTreeData(
                        localSelectedFolderKey || selectedFolderKey,
                        treeData?.children || []
                      )
                }
                placeholder="Select/create folder"
                suffixIcon={<ChevronDownIcon />}
                placement="bottom"
                onChange={(keys, info) => {
                  const [newValue] = info;

                  const selectedTree = keys.split('_');
                  // If new folder is being created
                  if (keys && typeof keys === 'string' && keys.match(/_new/)) {
                    // Store the folder name to select it after creation
                    setNewlyCreatedFolderName(newValue);
                    addNewChildFromSearch(newValue);
                  } else {
                    handleSelectTreeItem(selectedTree);
                  }
                }}
                onSearch={(value) => {
                  setTreeSearch(value);
                  handleTreeSearch(value);
                }}
              />
            </Row>

            <div className="button-wrapper">
              {catalogSource === 'xml' && (
                <Button
                  className="cancel-btn upload-xml-file-btn"
                  disabled={xmlFileList.length > 0 || parseXMLLoading}
                  onClick={() => {
                    // Create a hidden file input
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.xml';
                    input.onchange = (e) => {
                      const [file] = e.target.files || [];
                      if (file) {
                        // Validate file type
                        const isXML =
                          file.type === 'application/xml' ||
                          file.type === 'text/xml' ||
                          file.name.endsWith('.xml');
                        if (!isXML) {
                          message.error('Please upload a valid XML file');
                          return;
                        }
                        // Clear link when file is uploaded
                        setXmlLink('');
                        setXmlFileList([file]);
                        // Automatically parse the file
                        handleXMLFileUpload(file);
                      }
                    };
                    input.click();
                  }}>
                  Upload file
                </Button>
              )}
              <Button
                key="back"
                className="large-btn secondary-btn"
                onClick={handleUpload}
                loading={catalogSource === 'xml' ? parseXMLLoading : false}
                disabled={
                  catalogSource === 'csv'
                    ? fileList.length === 0
                    : !xmlLink && xmlFileList.length === 0
                }>
                START IMPORTING
              </Button>
            </div>
          </div>
        )}
      </Modal>
      <>
        {showModal && fileList.length > 0 && (
          <Popconfirm
            visible={showModal}
            title={'Do you want to exit?'}
            closable={false}
            cancelText={'Continue'}
            confirmText={'Yes, Exit'}
            onCancel={() => {
              setShowModal(false);
            }}
            onConfirm={onCancelModal}
            footer={null}
          />
        )}
      </>
    </>
  );
};

/**
 * Default props
 */
ImportCatalogModal.defaultProps = {
  treeData: {},
  showImportCatalogModal: false,
  setShowImportCatalogModal: () => {},
  defaultCatalogSource: 'csv',
  defaultSaveXmlLink: false,
};

/**
 * Props
 */
ImportCatalogModal.propTypes = {
  treeData: PropTypes.object,
  showImportCatalogModal: PropTypes.bool,
  setShowImportCatalogModal: PropTypes.func,
  defaultCatalogSource: PropTypes.string,
  defaultSaveXmlLink: PropTypes.bool,
};

export default ImportCatalogModal;
